import type { BoxShadowType } from '@vedalib/editor/lib/brand'
import { useCallback, useMemo, useState } from 'react'
import React from 'react'

import { FieldLayout } from 'components/formKit'
import type { AdapterKitControlProps, IFormKitControlProps } from 'components/formKit/types'
import ColorPicker, { ColorRoot } from 'components/uiKit/ColorPicker'
import { toColor } from 'components/uiKit/ColorPicker/helpers'
import DropDown from 'components/uiKit/Dropdown'
import { flatOptions } from 'components/uiKit/KitUtils'
import { isMixed } from 'components/uiKit/KitUtils'
import Slider from 'components/uiKit/Slider'
import { Typography } from 'components/uiKit/Typography'
import { t } from 'services/Translation'

import { genColorOptions } from '../../constants/constants'
import { useBrandTheme } from '../../hooks'
import * as s from './BrandFieldShadow.module.scss'
import Presets, { PRESETS } from './Presets'
import { safeParseBoxShadow } from './utils'

export interface IBrandCustomShadowFieldProps extends IFormKitControlProps<string> {
  inline?: boolean
  brand?: boolean
}

const BrandCustomShadowField = (
  props: AdapterKitControlProps<IBrandCustomShadowFieldProps, string>,
) => {
  const { value, name, size, disabled, defaultValue, inline, brand } = props
  const { onChange, onBlur, onFocus } = props
  const mixed = isMixed(value, defaultValue)
  const shadow = safeParseBoxShadow(
    mixed
      ? undefined
      : (value as string | null | undefined) || (defaultValue as string | null | undefined),
  )
  const theme = useBrandTheme()
  const options = useMemo(() => flatOptions(genColorOptions(theme)), [theme])
  const activeOption = options?.find((item) => item.value === shadow.color)
  const [visible, setVisible] = useState(false)
  const { hexa } = useMemo(() => toColor(shadow.color, options), [options, shadow.color])
  const isNone = shadow.preset === 'none'

  const handleChangePreset = (preset: BoxShadowType['preset']) => {
    onChange({ value: JSON.stringify({ ...shadow, preset }), name })
    if (preset === 'none') {
      setVisible(false)
    }
  }

  const handleChangeSize = useCallback(
    (size: number) => onChange({ value: JSON.stringify({ ...shadow, size }), name }),
    [onChange, name, shadow],
  )

  const handleChangeColor = useCallback(
    (color: string | null) => onChange({ value: JSON.stringify({ ...shadow, color }), name }),
    [onChange, name, shadow],
  )

  const controls = (
    <div className={s.root} tabIndex={-1}>
      <Presets
        brand={brand}
        disabled={disabled}
        name={name}
        onChange={onChange}
        onChangePreset={handleChangePreset}
        preset={shadow.preset}
      />
      {!isNone && (
        <FieldLayout label={t('input.label.shadowColor')} layout='toolbar'>
          <ColorPicker
            disabled={disabled}
            name={`${name}.color`}
            onChange={handleChangeColor}
            options={options}
            size={size}
            value={shadow.color}
            labeled
          />
        </FieldLayout>
      )}
      {!isNone && (
        <FieldLayout label={t('input.label.shadowSize')} layout='toolbar'>
          <Slider
            disabled={disabled}
            max={20}
            min={-20}
            name={`${name}.size`}
            onChange={handleChangeSize}
            size={size}
            value={shadow.size}
            showInput
          />
        </FieldLayout>
      )}
    </div>
  )

  if (inline) {
    return controls
  }

  return (
    <>
      {mixed && <Typography styleType='placeholder'>{t('editorV2.shadow.mixed')}</Typography>}
      <DropDown
        onBlur={onBlur as () => void}
        onFocus={onFocus as () => void}
        onVisibleChange={setVisible}
        overlay={controls}
        placement='top-end'
        visible={visible}
      >
        {({ open }) => (
          <ColorRoot
            active={open}
            color={hexa}
            forceLabel={PRESETS.find((item) => item.key === shadow.preset)?.label}
            name={name}
            option={activeOption}
            size={size}
            value={isNone ? null : shadow.color}
            labeled
          />
        )}
      </DropDown>
    </>
  )
}

export default BrandCustomShadowField
